import React, { useState, useEffect, Fragment } from 'react';
import {
  ReferenceArrayField,
  Datagrid,
  TextField as RaTextField,
  useTranslate,
  Button,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import isEmpty from 'lodash/isEmpty';
import makeStyles from '@material-ui/core/styles/makeStyles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { EditForm } from '~/components/ra';
import { H2, TableCell, EditButton, TextField, H3, GlTypeLabel } from '~/components';
import { useFeatures } from '~/hooks';
import { Section } from '~/layout';
import SolutionEdit from '../../SolutionEdit';
import { Solution } from '~/types';
import { useProductTemplates } from '~/hooks/useProductTemplates';
import { queryClient } from '~/App';
import { TenantType } from '~/api/utils';
import { tenantQueryKey } from '~/hooks/tenant';

interface EditProps {
  handleClose: () => void;
  resource: string;
  record: Solution;
  t: (key: string) => string;
}

const FeaturesEdit = (props: EditProps) => {
  const { handleClose, resource, record, t } = props;
  const features: string[] = record.features;
  const { data, loading, list } = useFeatures();
  const tenant = queryClient.getQueryData<TenantType>(tenantQueryKey);
  const isTenant = record.id === tenant?.id;
  const allFeatures = (data?.map((item) => item.key) || []).filter(
    (item) => !item.startsWith('veengu_unbound') && item.startsWith('veengu_tenant') === isTenant
  );
  const [state, setState] = useState<{ [x: string]: boolean }>({});

  console.log('features:', features, data, state);
  const isListEmpty = isEmpty(list);
  const isStateEmpty = isEmpty(state);

  useEffect(() => {
    if (!isListEmpty && isStateEmpty) {
      const result: { [x: string]: boolean } = {};
      features.forEach((key) => (result[key] = true));
      setState(result);
    }
  }, [isListEmpty, features, isStateEmpty]);

  const handleChange = (key: string, checked: boolean) => () => {
    setState((prev) => ({ ...prev, [key]: !checked }));
  };

  const newFeatures = Object.keys(state).filter((key) => state[key]);
  const handleSubmit = (_: any, submitQuery: (newRecord: any) => void): void => {
    submitQuery({
      ...record,
      features: newFeatures,
    });
  };

  const classes = useStyles();

  const categorizedFeatures = categorizeFeatures(allFeatures);

  return (
    <EditForm<Solution>
      closeParent={handleClose}
      resource={resource}
      record={record}
      onSubmit={handleSubmit}
      externalState={{
        ...record,
        features: newFeatures,
      }}
    >
      {['individual', 'business', 'partner', 'other']
        .map((key) => [key, (categorizedFeatures as any)[key]])
        .map(([category, categoryFeatures]) => {
          if (loading) {
            return (
              <React.Fragment key={category}>
                <H3
                  bottomSpacing={0}
                  topSpacing={0}
                  style={{ marginLeft: 4, marginTop: 16, marginBottom: 8 }}
                >
                  {t(`category.${category}`)}
                </H3>
                <Table style={{ width: '100%' }}>
                  <TableBody>
                    {loading &&
                      new Array(10).fill(0).map((_, index) => (
                        <TableRow key={index} style={{ width: '100%' }}>
                          <TableCell loading={true} style={{ width: '100%' }} />
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </React.Fragment>
            );
          }

          if (categoryFeatures.length === 0) {
            return null;
          }

          return (
            <React.Fragment key={category}>
              <H3
                bottomSpacing={0}
                topSpacing={0}
                style={{ marginLeft: 4, marginTop: 16, marginBottom: 8 }}
              >
                {t(`category.${category}`)}
              </H3>
              <Table style={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{t('featureName')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryFeatures?.map((featureKey: string) => {
                    const name = data.find((item) => item.key === featureKey)?.name || featureKey;
                    const checked = Boolean(state[featureKey]);
                    return (
                      <TableRow key={featureKey}>
                        <TableCell classes={{ root: classes.checkboxCell }}>
                          <Checkbox
                            checked={checked}
                            onChange={handleChange(featureKey, checked)}
                            className={classes.checkbox}
                          />
                        </TableCell>
                        <TableCell>{name}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </React.Fragment>
          );
        })}
    </EditForm>
  );
};

const ProductTemplatesEdit = (props: EditProps) => {
  const { handleClose, resource, record, t } = props;
  const productTemplates: string[] = record.productTemplates || [];

  const { data } = useProductTemplates();

  const [state, setState] = useState<{ [x: string]: boolean }>({});

  const isListEmpty = isEmpty(data);
  const isStateEmpty = isEmpty(state);

  useEffect(() => {
    if (!isListEmpty && isStateEmpty) {
      const result: { [x: string]: boolean } = {};
      productTemplates.forEach((key) => (result[key] = true));
      setState(result);
    }
  }, [isListEmpty, productTemplates, isStateEmpty]);

  const handleChange = (key: string, checked: boolean) => () => {
    setState((prev) => ({ ...prev, [key]: !checked }));
  };

  const newProductTemplate = Object.keys(state).filter((key) => state[key]);
  const handleSubmit = (_: any, submitQuery: (newRecord: any) => void): void => {
    submitQuery({
      ...record,
      productTemplates: newProductTemplate,
    });
  };

  const classes = useStyles();

  return (
    <EditForm<Solution>
      closeParent={handleClose}
      resource={resource}
      record={record}
      onSubmit={handleSubmit}
      externalState={{
        ...record,
        productTemplates: newProductTemplate,
      }}
    >
      <Table style={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>{t('templateAlias')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((item) => {
            const checked = Boolean(state[item.code]);

            return (
              <TableRow key={item.id}>
                <TableCell classes={{ root: classes.checkboxCell }}>
                  <Checkbox
                    checked={checked}
                    onChange={handleChange(item.code, checked)}
                    className={classes.checkbox}
                  />
                </TableCell>
                <TableCell>
                  <GlTypeLabel glType={item.glType} text={item.alias} />
                </TableCell>
              </TableRow>
            );
          })}
          {!data &&
            [{}, {}, {}].map((_, index) => (
              <TableRow key={index}>
                <TableCell loading={true} />
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </EditForm>
  );
};

export interface Categories {
  business: string[];
  individual: string[];
  partner: string[];
  other: string[];
}

export const categorizeFeatures = (features: string[]) => {
  const categories: Categories = {
    business: [] as string[],
    individual: [] as string[],
    partner: [] as string[],
    other: [] as string[],
  };

  features.forEach((feature) => {
    if (feature.startsWith('veengu_business_')) {
      categories.business.push(feature);
    } else if (feature.startsWith('veengu_individual_')) {
      categories.individual.push(feature);
    } else if (feature.startsWith('veengu_partner')) {
      categories.partner.push(feature);
    } else {
      categories.other.push(feature);
    }
  });

  categories.individual = categories.individual.sort((a, b) => a.localeCompare(b));
  categories.business = categories.business.sort((a, b) => a.localeCompare(b));
  categories.partner = categories.partner.sort((a, b) => a.localeCompare(b));
  categories.other = categories.other.sort((a, b) => a.localeCompare(b));

  return categories;
};

const FeaturesGrid = (props: SummaryTabProps) => {
  const { resource, record, permissions } = props;
  const features: string[] = record?.features || [];

  // Categorize features by their prefix
  const categorizedFeatures = categorizeFeatures(features);

  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.solutions.fields.${key}`);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const classes = useStyles();

  const editAllowed = permissions?.includes('solution.update');

  return (
    <Grid container xs={4} className={classes.table} item direction='column' spacing={1}>
      <Grid item container justify='space-between' alignItems='center'>
        <Grid item>
          <H2 bottomSpacing={0} topSpacing={0}>
            {t('features')}
          </H2>
        </Grid>

        {editAllowed && (
          <Grid item>
            {!open ? (
              <EditButton onClick={handleOpen} />
            ) : (
              <Button label={'ra.action.cancel'} onClick={handleClose} />
            )}
          </Grid>
        )}
      </Grid>
      <Grid item container spacing={1}>
        {open ? (
          <FeaturesEdit handleClose={handleClose} resource={resource} record={record} t={t} />
        ) : !isEmpty(features) ? (
          ['individual', 'business', 'partner', 'other']
            .map((key) => [key, (categorizedFeatures as any)[key]])
            .map(
              ([category, categoryFeatures]) =>
                // Only render categories that have features
                (categoryFeatures as any).length > 0 && (
                  <Fragment key={category}>
                    <H3
                      bottomSpacing={0}
                      topSpacing={0}
                      style={{ marginLeft: 4, marginTop: 16, marginBottom: 8 }}
                    >
                      {t(`category.${category}`)}
                    </H3>
                    <ReferenceArrayField
                      reference='features'
                      source='id'
                      record={{ id: categoryFeatures }} // Pass the feature IDs as the 'id' property
                    >
                      <Datagrid>
                        <RaTextField source='name' label={t('featureName')} />
                      </Datagrid>
                    </ReferenceArrayField>
                  </Fragment>
                )
            )
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('featureName')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{t('noFeatures')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Grid>
    </Grid>
  );
};

const ProductTemplatesGrid = (props: SummaryTabProps) => {
  const { resource, record, permissions } = props;

  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.solutions.fields.${key}`);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const classes = useStyles();

  const editAllowed = permissions?.includes('solution.update');

  const { data: productTemplates } = useProductTemplates();

  const selectedProductTemplates = productTemplates?.filter(
    (pt) => !!(record.productTemplates || []).find((code) => pt.code === code)
  );

  return (
    <Fragment>
      <Grid container xs={4} className={classes.table} item direction='column' spacing={1}>
        <Grid item container justify='space-between' alignItems='center'>
          <Grid item>
            <H2 bottomSpacing={0} topSpacing={0}>
              {t('productTemplates')}
            </H2>
          </Grid>
          {editAllowed && (
            <Grid item>
              {!open ? (
                <EditButton onClick={handleOpen} />
              ) : (
                <Button label={'ra.action.cancel'} onClick={handleClose} />
              )}
            </Grid>
          )}
        </Grid>
        <Grid item>
          {open ? (
            <ProductTemplatesEdit
              handleClose={handleClose}
              resource={resource}
              record={record}
              t={t}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('templateAlias')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty(selectedProductTemplates) ? (
                  selectedProductTemplates!.map((pt) => {
                    return (
                      <TableRow key={pt.code}>
                        <TableCell>
                          <GlTypeLabel glType={pt.glType} text={pt.alias} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell>{t('noProductTemplate')}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Grid>
      </Grid>
    </Fragment>
  );
};

interface SummaryTabProps {
  resource: string;
  record: Solution;
  permissions: string[];
  [x: string]: any;
}

const SummaryTab = (props: SummaryTabProps) => {
  const { resource, record, permissions } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.solutions.fields.${key}`);

  const classes = useStyles();

  return (
    <Grid container spacing={2} direction='column'>
      <FeaturesGrid {...props} />
      <ProductTemplatesGrid {...props} />
      <Section noPaddings>
        <Grid container item justify='space-between' className={classes.heading}>
          <Grid item>
            <H2 bottomSpacing={0} topSpacing={0}>
              {t('solutionDefinition')}
            </H2>
          </Grid>
          <Grid item id='section-actions'>
            <SolutionEdit record={record} resource={resource} permissions={permissions} />
          </Grid>
        </Grid>
        <Section>
          <Grid container item spacing={2}>
            <Grid item>
              <TextField label={t('name')}>{record?.name}</TextField>
            </Grid>
          </Grid>
        </Section>
      </Section>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  table: {
    // minWidth: 400,
  },
  checkbox: {
    margin: -16,
    marginLeft: -11,
  },
  checkboxCell: {
    paddingRight: 0,
  },
  heading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

export default SummaryTab;
